import React, { FormEvent, FunctionComponent, KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useFlash } from '../../../hooks/useFlash';
import { getCsrfToken } from '../../../libs/csrfToken';
import { scrollToElementById, scrollToTop } from '../../../libs/scrollUtils';
import { CarefulRegistrationProps, FieldErrors, HistoryItem, InformationForm, Step } from './types';
import { Presentation } from './presentation';

const emptyEducation = (): HistoryItem => ({
  name: '',
  department: '',
  startYear: '',
  startMonth: '',
  endYear: '',
  endMonth: '',
});

const emptyCareer = (): HistoryItem => ({
  name: '',
  position: '',
  startYear: '',
  startMonth: '',
  endYear: '',
  endMonth: '',
  currentWorking: false,
});

const firstErrorKey = (errors: FieldErrors) => {
  const priorities = [
    'last_name',
    'first_name',
    'birthday',
    'base',
    'occupation_id',
    'graduated_at',
    'preferred_occupation_id',
    'prefecture_id',
    'scout_flag',
    'email',
    'password',
    'agreement',
    'skills',
    'interests',
    'introduction',
    'slogan',
  ];
  return priorities.find(key => errors[key]?.length) || Object.keys(errors)[0];
};

const errorLabels: Record<string, string> = {
  last_name: '姓',
  first_name: '名',
  birthday: '生年月日',
  occupation_id: '直近の職種',
  graduated_at: '卒業予定年',
  preferred_occupation_id: '希望職種',
  prefecture_id: '希望勤務地',
  scout_flag: 'スカウト',
  email: 'メールアドレス',
  password: 'パスワード',
  agreement: '利用規約',
  skills: 'スキル・特徴・資格',
  interests: '興味・関心',
  introduction: '紹介文',
  slogan: '挑戦したいこと',
};

const presenceErrorMessage = (key: string) => `${errorLabels[key] || key}を入力してください`;

const normalizeFieldMessages = (key: string, messages: string[]) => {
  return messages.map(message => {
    if (key === 'agreement' || message.includes('accepted') || message.includes('同意') || message.includes('受諾')) {
      return '利用規約に同意してください';
    }
    if (key === 'email' && message.includes('既に登録')) {
      return 'メールアドレスは既に登録されています。';
    }
    if (key === 'email' && (message.includes('invalid') || message.includes('不正') || message.includes('正しく'))) {
      return 'メールアドレスを正しく入力してください';
    }
    if (key === 'password' && (message.includes('too_short') || message.includes('短') || message.includes('文字'))) {
      return 'パスワードは6文字以上で入力してください';
    }
    if (message.includes('blank') || message.includes('入力') || message === 'を入力してください') {
      return presenceErrorMessage(key);
    }
    if (message.includes('invalid') || message.includes('正しく')) {
      return `${errorLabels[key] || key}を正しく入力してください`;
    }
    return message;
  });
};

const birthdayErrorMessages = (information: InformationForm, errors: FieldErrors) => {
  const hasBirthdayError = !!(
    errors.birthday?.length ||
    errors.base?.length ||
    errors.birthday_1i?.length ||
    errors.birthday_2i?.length ||
    errors.birthday_3i?.length
  );

  if (!hasBirthdayError) return [];

  const missingParts = [
    !information.birthdayYear && '年',
    !information.birthdayMonth && '月',
    !information.birthdayDay && '日',
  ].filter(Boolean);

  return missingParts.length
    ? [`生年月日（${missingParts.join('/')}）を入力してください`]
    : ['生年月日を正しく入力してください'];
};

const normalizeErrors = (errors: FieldErrors, information: InformationForm): FieldErrors => {
  const normalized = { ...errors };
  Object.entries(normalized).forEach(([key, messages]) => {
    if (key === 'scout_flag') {
      if (information.scoutFlag) {
        delete normalized[key];
      } else {
        normalized[key] = [presenceErrorMessage(key)];
      }
      return;
    }
    if (key === 'email' && !information.email) {
      normalized[key] = [presenceErrorMessage(key)];
      return;
    }
    if (key === 'password' && !information.password) {
      normalized[key] = [presenceErrorMessage(key)];
      return;
    }
    if (key === 'agreement' && !information.agreement) {
      normalized[key] = ['利用規約に同意してください'];
      return;
    }
    normalized[key] = Array.from(new Set(normalizeFieldMessages(key, messages)));
  });

  const birthdayMessages = birthdayErrorMessages(information, errors);
  if (birthdayMessages.length) {
    normalized.birthday = birthdayMessages;
  }
  delete normalized.base;
  delete normalized.birthday_1i;
  delete normalized.birthday_2i;
  delete normalized.birthday_3i;

  return normalized;
};

const toastErrorMessage = (errors: FieldErrors, fallback: string) => {
  const messages = Array.from(new Set(Object.entries(errors).flatMap(([, values]) => values)));
  return messages.length ? messages.join(' / ') : fallback;
};

const informationErrorKeys: Partial<Record<keyof InformationForm, string[]>> = {
  lastName: ['last_name'],
  firstName: ['first_name'],
  birthdayYear: ['birthday'],
  birthdayMonth: ['birthday'],
  birthdayDay: ['birthday'],
  occupationId: ['occupation_id'],
  graduatedAt: ['graduated_at'],
  preferredOccupationId: ['preferred_occupation_id'],
  prefectureId: ['prefecture_id'],
  scoutFlag: ['scout_flag'],
  email: ['email'],
  password: ['password'],
  agreement: ['agreement'],
};

export const Component: FunctionComponent<CarefulRegistrationProps> = ({
  mode = 'careful',
  initialStep,
  paths,
  apiPaths,
  assets,
  options,
  links,
  secureToken,
  formSource,
  showScout = true,
  companyMessage,
  tokenErrorMessage,
  provider = '',
  uid = '',
  email = '',
  flash: initialFlash,
  flashType: initialFlashType,
  flashMessage: initialFlashMessage,
}) => {
  const { flash, flashType, flashMessage, handleFlash } = useFlash();

  useEffect(() => {
    if (initialFlash) {
      handleFlash(initialFlash, initialFlashType || '', initialFlashMessage || '');
    }
  }, []);

  const [step, setStep] = useState<Step>(initialStep);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [informationExpanded, setInformationExpanded] = useState(false);
  const [information, setInformation] = useState<InformationForm>({
    lastName: '',
    firstName: '',
    birthdayYear: '',
    birthdayMonth: '',
    birthdayDay: '',
    occupationId: '',
    occupationDetailId: '',
    graduatedAt: '',
    preferredOccupationId: '',
    preferredOccupationDetailId: '',
    prefectureId: '',
    scoutFlag: showScout ? 'scoutable' : '',
    email,
    password: '',
    agreement: false,
    provider,
    uid,
  });
  const [educationalHistories, setEducationalHistories] = useState<HistoryItem[]>([emptyEducation()]);
  const [careerHistories, setCareerHistories] = useState<HistoryItem[]>([emptyCareer()]);
  const [skills, setSkills] = useState<string[]>([]);
  const [interests, setInterests] = useState<string[]>([]);
  const [skillInput, setSkillInput] = useState('');
  const [interestInput, setInterestInput] = useState('');
  const [condition, setCondition] = useState({ motivation: '', introduction: '', slogan: '' });

  const changeStep = useCallback((nextStep: Step, push = true) => {
    setStep(nextStep);
    setErrors({});
    if (push && window.location.pathname !== paths[nextStep]) {
      window.history.pushState({ step: nextStep }, '', paths[nextStep]);
    }
    scrollToTop(undefined, 0, 'smooth');
  }, [paths]);

  useEffect(() => {
    const syncStep = () => {
      const matched = (Object.keys(paths) as Step[]).find(key => paths[key] === window.location.pathname);
      if (matched) changeStep(matched, false);
    };
    window.addEventListener('popstate', syncStep);
    return () => window.removeEventListener('popstate', syncStep);
  }, [changeStep, paths]);

  const submitJson = async (url: string, body: object) => {
    setSubmitting(true);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) {
        const responseErrors = normalizeErrors(data.errors || {}, information);
        setErrors(responseErrors);
        handleFlash(true, 'danger', toastErrorMessage(responseErrors, data.message || '入力内容を確認してください'));
        setTimeout(() => {
          const key = firstErrorKey(responseErrors);
          const targetKey = key === 'first_name' ? 'last_name' : key;
          if (targetKey) scrollToElementById(`careful-field-${targetKey}`);
        }, 0);
        return null;
      }
      return data as { message: string; nextPath?: string; redirect_url?: string };
    } catch {
      handleFlash(true, 'danger', '通信に失敗しました。時間をおいて再度お試しください');
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  const updateInformation = (field: keyof InformationForm, value: string | boolean) => {
    const errorKeys = informationErrorKeys[field] || [];
    if (errorKeys.length) {
      setErrors(prev => {
        const next = { ...prev };
        errorKeys.forEach(key => delete next[key]);
        return next;
      });
    }
    setInformation(prev => {
      if (field === 'occupationId') {
        return {
          ...prev,
          occupationId: value as string,
          occupationDetailId: '',
          graduatedAt: value === '15' ? prev.graduatedAt : '',
        };
      }
      if (field === 'preferredOccupationId') {
        return {
          ...prev,
          preferredOccupationId: value as string,
          preferredOccupationDetailId: '',
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const occupationDetails = useMemo(() => {
    if (!information.occupationId) return [];
    return options.occupationDetails.filter(option => String(option.occupationId) === information.occupationId);
  }, [information.occupationId, options.occupationDetails]);

  const preferredOccupationDetails = useMemo(() => {
    if (!information.preferredOccupationId) return [];
    return options.occupationDetails.filter(option => String(option.occupationId) === information.preferredOccupationId);
  }, [information.preferredOccupationId, options.occupationDetails]);

  const submitInformation = async (event: FormEvent) => {
    event.preventDefault();
    if (!informationExpanded) {
      setInformationExpanded(true);
      setTimeout(() => scrollToElementById('careful-field-occupation_id'), 0);
      return;
    }
    if (showScout && !information.scoutFlag) {
      const scoutErrors = { scout_flag: ['スカウトを入力してください'] };
      setErrors(prev => ({ ...prev, ...scoutErrors }));
      handleFlash(true, 'danger', scoutErrors.scout_flag[0]);
      scrollToElementById('careful-field-scout_flag');
      return;
    }
    const result = await submitJson(apiPaths.information, {
      people_new_form: {
        last_name: information.lastName,
        first_name: information.firstName,
        birthday_1i: information.birthdayYear,
        birthday_2i: information.birthdayMonth,
        birthday_3i: information.birthdayDay,
        occupation_id: information.occupationId,
        occupation_detail_id: information.occupationDetailId,
        graduated_at: information.graduatedAt,
        preferred_occupation_id: information.preferredOccupationId,
        preferred_occupation_detail_id: information.preferredOccupationDetailId,
        prefecture_id: information.prefectureId,
        scout_flag: information.scoutFlag,
        require_scout: showScout,
        email: information.email,
        password: information.password,
        agreement: information.agreement ? '1' : '0',
        secure_token: secureToken,
        form_source: formSource,
        provider: information.provider,
        uid: information.uid,
      },
    });
    if (result) {
      if (mode === 'basic') {
        if (result.redirect_url) {
          window.location.assign(result.redirect_url);
        }
        return;
      }
      handleFlash(true, 'success', result.message);
      changeStep('history');
    }
  };

  const updateHistory = (
    type: 'education' | 'career',
    index: number,
    field: keyof HistoryItem,
    value: string | boolean,
  ) => {
    const setter = type === 'education' ? setEducationalHistories : setCareerHistories;
    setter(prev => prev.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item)));
  };

  const submitHistory = async (event: FormEvent) => {
    event.preventDefault();
    const result = await submitJson(apiPaths.history, {
      people_registration_careful_form: {
        educational_histories: educationalHistories.map(history => ({
          name: history.name,
          department: history.department,
          start_year: history.startYear,
          start_month: history.startMonth,
          end_year: history.endYear,
          end_month: history.endMonth,
        })),
        career_histories: careerHistories.map(history => ({
          name: history.name,
          position: history.position,
          start_year: history.startYear,
          start_month: history.startMonth,
          end_year: history.endYear,
          end_month: history.endMonth,
          current_working: history.currentWorking ? 'true' : 'false',
        })),
      },
    });
    if (result) changeStep('skill');
  };

  const addTag = (type: 'skill' | 'interest', value: string) => {
    const tag = value.trim();
    if (!tag) return;
    const setter = type === 'skill' ? setSkills : setInterests;
    setter(prev => (prev.includes(tag) ? prev : [...prev, tag]));
    if (type === 'skill') setSkillInput('');
    if (type === 'interest') setInterestInput('');
  };

  const removeTag = (type: 'skill' | 'interest', value: string) => {
    const setter = type === 'skill' ? setSkills : setInterests;
    setter(prev => prev.filter(tag => tag !== value));
  };

  const tagKeyDown = (type: 'skill' | 'interest') => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault();
      addTag(type, type === 'skill' ? skillInput : interestInput);
    }
  };

  const submitSkill = async (event: FormEvent) => {
    event.preventDefault();
    addTag('skill', skillInput);
    addTag('interest', interestInput);
    const nextSkills = skillInput.trim() && !skills.includes(skillInput.trim()) ? [...skills, skillInput.trim()] : skills;
    const nextInterests = interestInput.trim() && !interests.includes(interestInput.trim()) ? [...interests, interestInput.trim()] : interests;
    const result = await submitJson(apiPaths.skill, {
      people_registration_careful_form: {
        skills: nextSkills.map(title => ({ title })),
        interests: nextInterests.map(title => ({ title })),
      },
    });
    if (result) changeStep('condition');
  };

  const submitCondition = async (event: FormEvent) => {
    event.preventDefault();
    const result = await submitJson(apiPaths.condition, {
      people_registration_careful_form: condition,
    });
    if (result?.nextPath) window.location.href = result.nextPath;
  };

  return (
    <Presentation
      assets={assets}
      mode={mode}
      companyMessage={companyMessage}
      tokenErrorMessage={tokenErrorMessage}
      condition={condition}
      careerHistories={careerHistories}
      educationalHistories={educationalHistories}
      errors={errors}
      flash={flash}
      flashMessage={flashMessage}
      flashType={flashType}
      handleFlash={handleFlash}
      information={information}
      informationExpanded={informationExpanded}
      interestInput={interestInput}
      interests={interests}
      links={links}
      occupationDetails={occupationDetails}
      options={options}
      preferredOccupationDetails={preferredOccupationDetails}
      skillInput={skillInput}
      skills={skills}
      step={step}
      showScout={showScout}
      submitting={submitting}
      addCareerHistory={() => setCareerHistories(prev => [...prev, emptyCareer()])}
      addEducationalHistory={() => setEducationalHistories(prev => [...prev, emptyEducation()])}
      addTag={addTag}
      removeTag={removeTag}
      setCondition={setCondition}
      setInterestInput={setInterestInput}
      setSkillInput={setSkillInput}
      submitCondition={submitCondition}
      submitHistory={submitHistory}
      submitInformation={submitInformation}
      submitSkill={submitSkill}
      tagKeyDown={tagKeyDown}
      updateHistory={updateHistory}
      updateInformation={updateInformation}
    />
  );
};
